import { compressCodeArray } from '../../utils/filter'
import type { ConstructDefinition, ConstructItem, ConstructLevel } from '../../types/gotypes'

// builderModel is the pure half of the construct builder: it turns what a
// person built (lines of conditions, a crossed grid) into
// the construct definition goapi saves, and reads a saved definition back.
//
// Carbon syntax matters here (docs/carbon.md, "Separators"): codes inside
// var(...) are separated by ";" and "a/b" is a RANGE. Joining picked codes
// with "/" turns North+South into North..South, silently.

export interface Condition {
  variable: string
  // The chosen codes, or [ANY_ANSWER] for the whole variable ("any answer",
  // Carbon var(*): the respondent answered it at all).
  codes: string[]
  // "is not": the respondent must have none of these codes (Carbon "!").
  not?: boolean
  // Starts a new or-row: joined to the conditions before it by OR rather
  // than AND. Ignored on the first condition.
  or?: boolean
}

export interface Group {
  label: string
  conditions: Condition[]
  // A saved rule the builder can't show as conditions (written by `pt
  // construct` or by hand, e.g. "age>=18"). Kept verbatim so a reopen and
  // save never changes it.
  rawRule?: string
}

// MergeGroup is one column of a cross: one or more of the second
// question's codes, merged under one label.
export interface MergeGroup {
  label: string
  codes: string[]
}

export interface CrossState {
  rows: string
  cols: string
  rowCodes: string[]
  // The row categories' labels as saved, so a reopen keeps them even before
  // (or without) the variable's categories loading.
  rowLabels: Record<string, string>
  colGroups: MergeGroup[]
}

// 'groups' is the Codes tab (Ruby's construct lines: one code each);
// 'cross' is Cross two variables. There was a third, Merge categories, until
// 2026-09-29: what it saved reopens in Codes (definitionToBuilder).
export type BuilderMode = 'groups' | 'cross'

export interface BuilderState {
  mode: BuilderMode
  groups: Group[]
  cross: CrossState | null
}

// ANY_ANSWER is the code list of a whole-variable condition: Carbon's
// all-codes "*" (docs/carbon.md), written var(*). It is the only code in its
// list; compressCodeArray passes it through as it is.
export const ANY_ANSWER = '*'
export const isAnyAnswer = (codes: string[]): boolean => codes.length === 1 && codes[0] === ANY_ANSWER

export const codesToCarbon = (codes: string[]): string => compressCodeArray([...new Set(codes)])

// parseCodes reads a plain code list ("1/3;7") back into codes. Anything else
// Carbon allows inside the brackets ("*", comparisons) is not a list the
// builder can show, so it is refused rather than guessed at.
export const parseCodes = (text: string): string[] | null => {
  const codes: string[] = []
  for (const part of text.split(';')) {
    const range = part.match(/^(\d+)\/(\d+)$/)
    if (range) {
      const from = parseInt(range[1], 10)
      const to = parseInt(range[2], 10)
      if (to < from) return null
      for (let n = from; n <= to; n++) codes.push(String(n))
      continue
    }
    if (!/^\d+$/.test(part)) return null
    codes.push(part)
  }
  return codes
}

// toRows splits a group's conditions into its or-rows (each row is ands);
// fromRows joins rows back, dropping empty ones.
export const toRows = (conditions: Condition[]): Condition[][] => {
  const rows: Condition[][] = []
  conditions.forEach((c, i) => {
    const { or, ...rest } = c
    if (i === 0 || !or) {
      if (rows.length === 0) rows.push([])
      rows[rows.length - 1].push(rest)
    } else {
      rows.push([rest])
    }
  })
  return rows
}

export const fromRows = (rows: Condition[][]): Condition[] =>
  rows
    .filter(r => r.length > 0)
    .flatMap((row, ri) => row.map((c, ci) => {
      const rest: Condition = { ...c }
      delete rest.or
      return ri > 0 && ci === 0 ? { ...rest, or: true } : rest
    }))

const conditionToFilter = (c: Condition) => `${c.not ? '!' : ''}${c.variable}(${codesToCarbon(c.codes)})`

// Carbon binds ! tighter than &, and & tighter than | (Defines.cs), so the
// brackets round a multi-condition row are for the reader, not the parser.
export const conditionsToFilter = (conditions: Condition[]): string => {
  const rows = toRows(conditions.filter(c => c.variable && c.codes.length > 0))
  return rows
    .map(row => {
      const text = row.map(conditionToFilter).join('&')
      return rows.length > 1 && row.length > 1 ? `(${text})` : text
    })
    .join('|')
}

const CONDITION = /^(!?)\s*([a-z][a-z0-9_]*)\s*\(([^()]*)\)$/i

// splitTop splits on a separator only where it is not inside brackets.
const splitTop = (text: string, sep: string): string[] | null => {
  const parts: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (ch === '(') depth++
    else if (ch === ')') { depth--; if (depth < 0) return null }
    else if (ch === sep && depth === 0) { parts.push(text.slice(start, i)); start = i + 1 }
  }
  if (depth !== 0) return null
  parts.push(text.slice(start))
  return parts.map(p => p.trim())
}

// stripOuter removes one pair of brackets wrapping the whole text.
const stripOuter = (text: string): string => {
  if (!text.startsWith('(') || !text.endsWith(')')) return text
  const inner = text.slice(1, -1)
  return splitTop(inner, '\u0000') ? inner.trim() : text
}

// parseFilter reads rows of "[!]var(codes)" joined by & and |, with optional
// brackets round a row, back into conditions. Anything deeper (a bracket
// under a "!", a | inside an & , comparisons, functions) is null, and the
// builder keeps it as a text rule.
export const parseFilter = (filter: string): Condition[] | null => {
  if (!filter || !filter.trim()) return null
  const rows = splitTop(filter.trim(), '|')
  if (!rows) return null
  const conditions: Condition[] = []
  for (const [ri, rowText] of rows.entries()) {
    const terms = splitTop(stripOuter(rowText), '&')
    if (!terms) return null
    for (const [ti, term] of terms.entries()) {
      const m = term.match(CONDITION)
      if (!m) return null
      const text = m[3].replace(/\s+/g, '')
      const codes = text === ANY_ANSWER ? [ANY_ANSWER] : parseCodes(text)
      if (!codes) return null
      const cond: Condition = { variable: m[2], codes }
      if (m[1]) cond.not = true
      if (ri > 0 && ti === 0) cond.or = true
      conditions.push(cond)
    }
  }
  return conditions
}

// appendConditions adds conditions at the end of a group, joined to what is
// there by "and" (and to each other, as the tree's selection is).
export const appendConditions = (group: Group, added: Condition[]): Group => ({
  ...group,
  conditions: [...group.conditions, ...added.map(c => {
    const rest: Condition = { ...c }
    delete rest.or
    return rest
  })],
})

// setJoin sets the and / or between condition index-1 and condition index:
// "or" starts a new or-row there (Condition.or), "and" joins its row.
export const setJoin = (conditions: Condition[], index: number, join: 'and' | 'or'): Condition[] =>
  conditions.map((c, i) => {
    if (i !== index) return c
    const rest: Condition = { ...c }
    delete rest.or
    return join === 'or' ? { ...rest, or: true } : rest
  })

// removeCondition takes one condition out, keeping the rows the rest were
// in: if it started an or-row, the next condition in that row starts it now.
export const removeCondition = (conditions: Condition[], index: number): Condition[] =>
  fromRows(toRows(conditions.map((c, i) => (
    i === index + 1 && conditions[index]?.or && !c.or ? { ...c, or: true } : c
  )).filter((_, i) => i !== index)))

export const groupsToItems = (groups: Group[]): ConstructItem[] => {
  const items: ConstructItem[] = []
  for (const g of groups) {
    const filter = g.conditions.length > 0 ? conditionsToFilter(g.conditions) : (g.rawRule || '')
    if (!filter) continue
    items.push({ code: items.length + 1, label: g.label, filter })
  }
  return items
}

// groupCodes is the code each group gets in the new variable, exactly as
// groupsToItems numbers them on save: 1..n in order, skipping a group with no
// rule (it is not saved, so it has no code: null).
export const groupCodes = (groups: Group[]): (number | null)[] => {
  let next = 0
  return groups.map(g => (ruleOf(g) ? ++next : null))
}

// ruleOf is a group's rule as it is saved: its conditions written out, or the
// text rule the builder cannot draw.
export const ruleOf = (group: Group): string =>
  group.conditions.length > 0 ? conditionsToFilter(group.conditions) : (group.rawRule || '')

// setRule is typing a group's rule. Text the builder can draw becomes its
// conditions (the cards); anything else is kept verbatim as a text rule, and
// that text is what is saved. Empty text leaves the group empty.
export const setRule = (group: Group, text: string): Group => {
  if (!text.trim()) return { label: group.label, conditions: [] }
  const conditions = parseFilter(text)
  return conditions ? { label: group.label, conditions } : { label: group.label, conditions: [], rawRule: text }
}

// onePerAnswer makes one line per answer of a question, labelled with the
// answer and ruled var(code): what Merge categories' "One line per answer"
// used to write, byte for byte.
export const onePerAnswer = (variable: string, answers: { code: string, label: string }[]): Group[] =>
  answers.map(a => ({ label: a.label, conditions: [{ variable, codes: [a.code] }] }))

export const crossToLevels = (
  rows: { variable: string, codes: { code: string, label: string }[] },
  cols: { variable: string, groups: MergeGroup[] },
): ConstructLevel[] => [
  { variable: rows.variable, items: rows.codes.map(c => ({ label: c.label, filter: `${rows.variable}(${c.code})` })) },
  {
    variable: cols.variable,
    items: cols.groups
      .filter(g => g.codes.length > 0)
      .map(g => ({ label: g.label, filter: `${cols.variable}(${codesToCarbon(g.codes)})` })),
  },
]

const singleVariableCodes = (filter: string, variable?: string): { variable: string, codes: string[] } | null => {
  const conds = parseFilter(filter)
  if (!conds || conds.length !== 1 || conds[0].not || isAnyAnswer(conds[0].codes)) return null
  if (variable && conds[0].variable !== variable) return null
  return conds[0]
}

const groupsFromItems = (items: ConstructItem[]): Group[] =>
  items.map(item => {
    const conditions = parseFilter(item.filter)
    return conditions ? { label: item.label, conditions } : { label: item.label, conditions: [], rawRule: item.filter }
  })

export const emptyBuilder = (): BuilderState => ({
  mode: 'groups',
  // Codes starts with none: the first green + makes the first code.
  groups: [],
  cross: null,
})

// definitionToBuilder picks the builder a saved definition reopens in: a
// two-level crossed net → cross; anything else → groups (Codes), one per
// item, with rules the builder cannot draw kept as raw text. A construct
// saved from the retired Merge categories tab (every rule one variable's
// codes, e.g. region(1;3)) is an ordinary code list, so it reopens as lines
// with one condition each.
export const definitionToBuilder = (def: ConstructDefinition): BuilderState => {
  const state = emptyBuilder()

  if (def.kind === 'crossedNet' && def.levels?.length === 2) {
    const [rowLevel, colLevel] = def.levels
    const rowCodes: string[] = []
    const rowLabels: Record<string, string> = {}
    let ok = true
    for (const item of rowLevel.items) {
      const parsed = singleVariableCodes(item.filter, rowLevel.variable)
      if (!parsed || parsed.codes.length !== 1) { ok = false; break }
      rowCodes.push(parsed.codes[0])
      rowLabels[parsed.codes[0]] = item.label
    }
    const colGroups: MergeGroup[] = []
    for (const item of colLevel.items) {
      const parsed = singleVariableCodes(item.filter, colLevel.variable)
      if (!parsed) { ok = false; break }
      colGroups.push({ label: item.label, codes: parsed.codes })
    }
    if (ok) {
      return { ...state, mode: 'cross', cross: { rows: rowLevel.variable, cols: colLevel.variable, rowCodes, rowLabels, colGroups } }
    }
  }

  const items = def.items || []
  return { ...state, mode: 'groups', groups: items.length > 0 ? groupsFromItems(items) : state.groups }
}

// suggestName offers a variable name from the category labels, obeying the
// server's rule: a letter first, then lower-case letters, digits, underscores.
export const suggestName = (labels: string[]): string => {
  const words = labels
    .join(' ')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .slice(0, 5)
  if (words.length === 0) return ''
  const name = words.join('_').slice(0, 60)
  return /^[a-z]/.test(name) ? name : `v_${name}`
}
